import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// ONE user's daily send allowance, read and written by an operator.
//
// The number lives at `sendPolicy/<uid>.dailySendCap` in Firestore and is read
// at send time by EmailAutomation's `_resolve_daily_send_cap_for_user`. This
// module never touches Firestore directly and could not: firestore.rules gives
// that document `allow write: if false`, so the browser has no path to it. Both
// the read and the two writes go through callables that use the Admin SDK.
//
// THE VALIDATION BELOW IS NOT THE RAIL. The callable validates again, with the
// same rules, and that is the one that matters. This copy exists so an operator
// finds out they typed "20" instead of 20 without a round trip, and so the same
// refusal message is written once.

export const MAX_USER_DAILY_SEND_CAP = 1000;

// Kept in step with functions/lib/sendPolicy.js and, through it, with
// email_automation/email.py.
const CAP_AUTHORS = ['operator', 'subscription'];
const LIMIT_SOURCES = ['user', 'platform', 'failClosed'];
const PLATFORM_DEFAULT_SOURCES = ['environment', 'builtin', 'malformed', 'disabled'];

const CAP_REFUSAL_MESSAGE =
  `A daily send limit must be a whole number from 1 to ${MAX_USER_DAILY_SEND_CAP}. `
  + 'Zero, negatives, fractions and text are refused: the send path would throw the '
  + 'value away and the user would silently keep the platform default.';

/**
 * A usable allowance, or null meaning refuse it.
 *
 * A mirror of `_coerce_user_daily_send_cap`. Refused rather than coerced,
 * because a value the send path discards is worse than no value at all: it
 * reads as configured while changing nothing.
 */
export function coerceDailySendCap(value) {
  if (typeof value === 'boolean') return null;
  if (typeof value !== 'number') return null;
  if (!Number.isFinite(value)) return null;
  if (!Number.isInteger(value)) return null;
  if (value <= 0 || value > MAX_USER_DAILY_SEND_CAP) return null;
  return value;
}

/** Parse what an operator typed. Text is a refusal, never a coercion attempt. */
export function parseTypedSendLimit(text) {
  const trimmed = String(text ?? '').trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return coerceDailySendCap(Number(trimmed));
}

function requiredUid(uid) {
  const normalized = typeof uid === 'string' ? uid.trim() : '';
  if (!normalized) {
    throw new TypeError('A user id is required to change a send limit');
  }
  return normalized;
}

/**
 * A count, or null. Null and zero are DIFFERENT here: null is "no ceiling at
 * all" (the rail is switched off) and zero would be "may send nothing". They
 * render identically in a table if either is allowed to become the other.
 */
function nullableCount(value) {
  if (value === null || value === undefined) return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized >= 0 ? Math.trunc(normalized) : null;
}

function count(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized >= 0 ? Math.trunc(normalized) : 0;
}

function text(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function oneOf(value, allowed, fallback) {
  return allowed.includes(value) ? value : fallback;
}

function normalizePlatformDefault(value) {
  return {
    dailySendCap: nullableCount(value?.dailySendCap),
    source: oneOf(value?.source, PLATFORM_DEFAULT_SOURCES, 'unknown'),
    configuredValue: typeof value?.configuredValue === 'string' ? value.configuredValue : null,
  };
}

function normalizeUser(value) {
  return {
    uid: String(value?.uid || ''),
    email: text(value?.email),
    displayName: text(value?.displayName),
    effectiveDailySendCap: nullableCount(value?.effectiveDailySendCap),
    limitSource: oneOf(value?.limitSource, LIMIT_SOURCES, 'unknown'),
    // Deliberately NOT normalized. When a stored allowance is unusable the
    // operator has to see the exact thing that is stored -- "300", 0, 20.5 --
    // to understand why the send path refuses it. Coercing it here would hide
    // the entire fault behind a plausible number.
    storedDailySendCap: value?.storedDailySendCap === undefined
      ? null
      : value.storedDailySendCap,
    decidedBy: text(value?.decidedBy),
    capPeriod: text(value?.capPeriod),
    usedToday: count(value?.usedToday),
    remainingToday: nullableCount(value?.remainingToday),
    updatedAt: text(value?.updatedAt),
    updatedBy: text(value?.updatedBy),
  };
}

export function normalizeSendLimitsReport(data) {
  const users = Array.isArray(data?.users) ? data.users : [];
  return {
    summary: {
      userCount: count(data?.summary?.userCount),
      withOwnAllowance: count(data?.summary?.withOwnAllowance),
      inheritingPlatformDefault: count(data?.summary?.inheritingPlatformDefault),
      failingClosed: count(data?.summary?.failingClosed),
    },
    platformDefault: normalizePlatformDefault(data?.platformDefault),
    dayKey: text(data?.dayKey),
    usageCaveat: {
      code: text(data?.usageCaveat?.code),
      message: text(data?.usageCaveat?.message),
    },
    users: users.map(normalizeUser),
  };
}

export async function requestAdminSendLimits({ firebaseFunctions = functions } = {}) {
  const callable = httpsCallable(firebaseFunctions, 'getAdminSendLimits');
  const response = await callable();
  return normalizeSendLimitsReport(response?.data);
}

/**
 * Give ONE user an allowance of their own.
 *
 * `decidedBy` is the seam Stripe will use. An operator's deliberate number is
 * protected from a plan overwriting it; the callable refuses that unless
 * `replaceOperatorAllowance` says so in as many words.
 */
export async function setUserSendLimit({
  firebaseFunctions = functions,
  uid,
  dailySendCap,
  decidedBy = 'operator',
  replaceOperatorAllowance = false,
} = {}) {
  const targetUid = requiredUid(uid);
  if (!CAP_AUTHORS.includes(decidedBy)) {
    throw new TypeError(`decidedBy must be one of: ${CAP_AUTHORS.join(', ')}`);
  }
  const cap = coerceDailySendCap(dailySendCap);
  if (cap === null) {
    throw new TypeError(CAP_REFUSAL_MESSAGE);
  }

  const payload = { uid: targetUid, dailySendCap: cap, decidedBy };
  if (replaceOperatorAllowance === true) payload.replaceOperatorAllowance = true;

  const callable = httpsCallable(firebaseFunctions, 'setUserSendLimit');
  const response = await callable(payload);
  return {
    uid: String(response?.data?.uid || targetUid),
    dailySendCap: nullableCount(response?.data?.dailySendCap),
    decidedBy: text(response?.data?.decidedBy),
    capPeriod: text(response?.data?.capPeriod),
  };
}

/**
 * Take ONE user's allowance away so they inherit the platform default again.
 *
 * A separate callable, not a set with a special number, because there is no
 * number that means this: the resolver falls through to the platform only when
 * the FIELD IS ABSENT, and 0 or null are values it refuses outright.
 */
export async function clearUserSendLimit({ firebaseFunctions = functions, uid } = {}) {
  const targetUid = requiredUid(uid);
  const callable = httpsCallable(firebaseFunctions, 'clearUserSendLimit');
  const response = await callable({ uid: targetUid });
  return {
    uid: String(response?.data?.uid || targetUid),
    dailySendCap: nullableCount(response?.data?.dailySendCap),
    decidedBy: text(response?.data?.decidedBy),
  };
}

export { CAP_AUTHORS, CAP_REFUSAL_MESSAGE };
